import { createContext, useContext, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { formatReportDate } from './dates'
import { FALLBACK_LOCALE, normalizeLocale } from './locales'
import en from './messages/en.json'
import es from './messages/es.json'
import esES from './messages/es_ES.json'
import frCA from './messages/fr_CA.json'
import ja from './messages/ja.json'
import nl from './messages/nl.json'
import sv from './messages/sv.json'

// The report can be rendered in any supported locale. The active locale is read
// from the `locale` query param (set by the backend when generating the PDF),
// falling back to English. Missing keys fall back to the English catalog
// automatically.
const CATALOGS = {
  en,
  es,
  es_ES: esES,
  fr_CA: frCA,
  ja,
  nl,
  sv,
}

// Catalog strings use the i18n `%{var}` placeholder convention (matching the
// backend), but `{var}` is also supported. Replace the `%{var}` form first so
// the leading `%` is consumed rather than left behind.
const interpolate = (template, vars) => {
  if (!vars) {
    return template
  }
  return Object.keys(vars).reduce((result, key) => {
    const value = vars[key] ?? ''
    return result
      .replaceAll(`%{${key}}`, String(value))
      .replaceAll(`{${key}}`, String(value))
  }, template)
}

// The default value has to accept the same arguments as the provider's
// implementation so components rendered outside ReportI18nProvider (unit and
// component tests, mostly) still honour the `short` option.
const ReportI18nContext = createContext({
  locale: FALLBACK_LOCALE,
  t: (key) => key,
  formatDate: (date, { short = false } = {}) =>
    formatReportDate({ date, locale: FALLBACK_LOCALE, short }),
})

// A document-fed renderer passes the locale its document was assembled in. The
// query param remains the source for the period-addressed report.
export const ReportI18nProvider = ({ children, locale: localeOverride }) => {
  const [searchParams] = useSearchParams()
  const locale = normalizeLocale(localeOverride || searchParams.get('locale'))

  // Reflect the active locale on the root element so Chromium bakes the matching
  // `/Lang` entry into the generated report PDF (accessibility + a verifiable
  // language marker even before translation catalogs are populated).
  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const value = useMemo(() => {
    const catalog = CATALOGS[locale] || CATALOGS[FALLBACK_LOCALE]
    const fallbackCatalog = CATALOGS[FALLBACK_LOCALE]

    // Resolve a key in the active locale, falling back to English, then to the
    // key itself so a missing string never blanks out the report.
    const t = (key, vars) => {
      const template = catalog[key] ?? fallbackCatalog[key] ?? key
      return interpolate(template, vars)
    }

    const formatDate = (date, { short = false } = {}) =>
      formatReportDate({ date, locale, short })

    return { locale, t, formatDate }
  }, [locale])

  return (
    <ReportI18nContext.Provider value={value}>
      {children}
    </ReportI18nContext.Provider>
  )
}

export const useReportTranslation = () => useContext(ReportI18nContext)
